import {computed, reactive, ref} from 'vue';
import {defineStore, storeToRefs} from 'pinia';
import {companiesAndOrganizationEventBus, useAuth, useLogger, useUniversalFetch} from '#imports';
import {readonlyState} from '@malt/utils';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import type {
    ClientCompaniesResponse,
    ClientCompanyResponse,
    ClientCurrentStructureResponse,
    ClientOrganizationResponse,
    SearchOrganizationResponse,
} from '../types/companies-and-organizations.models';

export type CompaniesAndOrganizationFilters = {
    organizations: string[];
    companies: string[];
};

export const useCompaniesAndOrganizationStore = defineStore('companies-and-organizations-store', () => {
    const logger = useLogger();

    const {user} = storeToRefs(useAuth());

    const isPlatformAdmin = computed(() => user.value?.platformAdmin || false);
    const clientCompanies = ref<ClientCompanyResponse[]>([]);
    const currentStructure = ref<ClientCurrentStructureResponse>();
    const organizations = ref<ClientOrganizationResponse[]>([]);
    const isLoadingOrganizations = ref(false);

    const filters = reactive<CompaniesAndOrganizationFilters>({
        organizations: [],
        companies: [],
    });

    async function fetchCurrentStructure() {
        try {
            currentStructure.value = await useUniversalFetch<ClientCurrentStructureResponse>('/api/registries/current-structure');
        } catch (error: any) {
            logAndDisplayError(error);
        }
    }

    async function fetchCompaniesForOrganization(organizationId?: string) {
        try {
            const queryParams = organizationId ? `?organizationId=${organizationId}` : '';
            const clientCompaniesResponse = await useUniversalFetch<ClientCompaniesResponse>(`/api/registries/companies${queryParams}`);
            if (isPlatformAdmin.value && organizationId && clientCompaniesResponse.companies.length === 0) {
                pushVJoySnackbar({props: {message: 'No company found associated to that organization, filters reset', level: 'error'}});
                companiesAndOrganizationEventBus.emit('filters:reset-all');
            }
            clientCompanies.value = clientCompaniesResponse.companies;
        } catch (error: any) {
            logAndDisplayError(error);
        }
    }

    async function searchOrganizations(query?: string) {
        isLoadingOrganizations.value = true;
        try {
            const currentStructureHasOrganization = currentStructure.value && currentStructure.value.organization;

            if (isPlatformAdmin.value) {
                const queryParams = query ? `?query=${query}` : '';
                const response = await useUniversalFetch<SearchOrganizationResponse>(`/api/registries/organizations${queryParams}`);

                if (currentStructureHasOrganization) {
                    const currentOrganization = currentStructure.value!.organization!;
                    const otherOrganizationSearchResult = response.organizations.filter(
                        (org: ClientOrganizationResponse) => org.id !== currentStructure.value!.organization!.id,
                    );

                    organizations.value = [currentOrganization, ...otherOrganizationSearchResult];
                } else {
                    organizations.value = response.organizations;
                }
            } else if (currentStructureHasOrganization) {
                organizations.value = [...(currentStructure.value?.organization ? [currentStructure.value.organization] : [])];
            }
        } catch (error: any) {
            logAndDisplayError(error);
        } finally {
            isLoadingOrganizations.value = false;
        }
    }

    function logAndDisplayError(error: any) {
        logger.error(error);
        const errorMessage = error.message ?? error;
        pushVJoySnackbar({props: {message: errorMessage, level: 'error'}});
    }

    return {
        clientCompanies: readonlyState(clientCompanies),
        currentStructure: readonlyState(currentStructure),
        filters,
        isLoadingOrganizations: readonlyState(isLoadingOrganizations),
        isPlatformAdmin: readonlyState(isPlatformAdmin),
        organizations: readonlyState(organizations),
        fetchCompaniesForOrganization,
        fetchCurrentStructure,
        searchOrganizations,
    };
});
